<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import type { NavItem } from '~/navigation/horizontal/index'
import { useWindowScroll } from '@vueuse/core'
import { computed, nextTick, ref, watch } from 'vue'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { useRouter } from 'vue-router'
import MobileMenu from '@/components/MobileMenu.vue'
import NavbarThemeSwitcher from '@/layouts/components/NavbarThemeSwitcher.vue'
import { useCartStore } from '@/stores/cart'
import { useGodmodeSlots } from '~/composables/useGodmodeSlots'
import { getNavigation } from '~/navigation/horizontal/index'
import { logApiError } from '~/utils/logger'

const props = defineProps({
  activeId: String,
})

// User data and authentication
const authStore = useAuthStore()
const userData = computed(() => authStore.userData)
const avatarLoadError = ref(false)
const cartStore = useCartStore()
const { slots: godmodeSlots } = useGodmodeSlots()
const cartParticipantCount = computed(() => godmodeSlots.value.length || cartStore.totalItems)

// Reset error state when avatar URL changes
watch(() => userData.value?.avatar, () => {
  avatarLoadError.value = false
})

const { getMyBookings } = useBookings()
const { getForms } = useForms()
const { can } = usePermissions()

const { y } = useWindowScroll()

const router = useRouter()
const route = useRoute()

// Track upcoming bookings count
const upcomingBookingsCount = ref(0)

// Track saved forms count
const savedFormsCount = ref(0)

// Centralized navigation data
const navigation = ref<NavItem[]>([])

// Responsive nav state (expanded → contact icon → compact)
type NavMode = 'expanded' | 'contact-icon' | 'compact'
const navMode = ref<NavMode>('expanded')
const isNarrowDesktop = computed(() => navMode.value === 'compact')
const showContactText = computed(() => navMode.value !== 'contact-icon')
const navbarLeftMenuRef = ref<HTMLElement | null>(null)
let resizeRaf = 0

// Touch device detection for proper menu behavior
const isTouchDevice = ref(false)

// Load navigation data from centralized config
async function loadNavigationData() {
  try {
    navigation.value = await getNavigation()
    await nextTick()
    updateNavMode()
  }
  catch (error) {
    logApiError('/navigation/horizontal', error, { operation: 'loadNavigationData' })
  }
}

// Fetch and compute upcoming bookings count
async function loadUpcomingBookingsCount() {
  if (!userData.value) {
    upcomingBookingsCount.value = 0

    return
  }

  try {
    const response = await getMyBookings({ status: 'confirmed' })
    if (response.success && response.data) {
      const now = new Date()

      // Filter bookings where the camp hasn't ended yet
      const upcoming = response.data.filter(booking => {
        const campEndDate = booking.camp?.end_date
        if (!campEndDate)
          return false

        return new Date(campEndDate) >= now
      })

      upcomingBookingsCount.value = upcoming.length
    }
  }
  catch (error) {
    logApiError('/api/bookings/my', error, { operation: 'loadUpcomingBookingsCount' })
    upcomingBookingsCount.value = 0
  }
}

// Fetch and compute saved forms count
async function loadSavedFormsCount() {
  if (!userData.value) {
    savedFormsCount.value = 0

    return
  }

  // Only load forms if user has permission to read forms
  if (!can('forms', 'read')) {
    savedFormsCount.value = 0

    return
  }

  try {
    const response = await getForms({ status: 'active', limit: 100 })
    if (response.data)
      savedFormsCount.value = response.data.length
  }
  catch (error) {
    logApiError('/api/forms', error, { operation: 'loadSavedFormsCount' })
    savedFormsCount.value = 0
  }
}

// Load upcoming bookings count when user data changes
watch(userData, newUserData => {
  if (newUserData) {
    loadUpcomingBookingsCount()
    loadSavedFormsCount()
  }
  else {
    upcomingBookingsCount.value = 0
    savedFormsCount.value = 0
  }
}, { immediate: true })

// Load navigation data on mount
onMounted(() => {
  loadNavigationData()
  handleResize()
  detectTouchDevice()
  window.addEventListener('resize', handleResize)
  if ('fonts' in document) {
    (document as Document & { fonts: FontFaceSet }).fonts.ready.then(() => {
      updateNavMode()
    })
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

// Detect touch device for proper menu behavior
function detectTouchDevice() {
  isTouchDevice.value = 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

function hasOverflow(el: HTMLElement) {
  return el.scrollWidth > el.clientWidth + 1
}

// Update responsive nav mode based on actual available width
async function updateNavMode() {
  if (window.innerWidth < 960) {
    navMode.value = 'compact'

    return
  }

  if (!navbarLeftMenuRef.value)
    return

  // 1) Try fully expanded with Contact text
  navMode.value = 'expanded'
  await nextTick()
  await new Promise(requestAnimationFrame)
  if (!navbarLeftMenuRef.value)
    return
  if (!hasOverflow(navbarLeftMenuRef.value))
    return

  // 2) Try Contact icon only
  navMode.value = 'contact-icon'
  await nextTick()
  await new Promise(requestAnimationFrame)
  if (!navbarLeftMenuRef.value)
    return
  if (!hasOverflow(navbarLeftMenuRef.value))
    return

  // 3) Compact menu (Aanbod)
  navMode.value = 'compact'
}

// Handle responsive breakpoint for narrow desktop
function handleResize() {
  if (resizeRaf)
    cancelAnimationFrame(resizeRaf)

  resizeRaf = requestAnimationFrame(() => {
    updateNavMode()
  })
}

async function logout() {
  try {
    // Use auth store logout method
    await authStore.logout()
  }
  catch (error) {
    // Suppress errors during logout - they're expected when clearing auth state
    // (e.g., Ability instance not available, RLS policy violations)
    if (process.dev)
      console.warn('Logout completed with warnings (this is normal):', error)
  }
  finally {
    // Always redirect to login page, regardless of errors
    await router.push({ name: 'auth-login' })
  }
}

const userProfileList = computed(() => {
  const allItems = [
    { type: 'divider' },
    {
      type: 'navItem',
      icon: 'bx-user',
      title: 'Profile',
      to: { name: 'user-profiel', query: { tab: 'account' } },
    },
    {
      type: 'navItem',
      icon: 'bx-credit-card',
      title: 'Mijn kampen',
      to: { name: 'user-profiel', query: { tab: 'bookings' } },
    },
    {
      type: 'navItem',
      icon: 'bx-award',
      title: 'Mijn attesten',
      to: { name: 'user-profiel', query: { tab: 'attesten' } },
    },
    {
      type: 'navItem',
      icon: 'bx-file',
      title: 'Mijn formulieren',
      to: { name: 'user-profiel', query: { tab: 'forms' } },
    },
    { type: 'divider', requiredPermission: { resource: 'admin_panel', action: 'access' } },
    {
      type: 'navItem',
      title: 'Dashboard',
      icon: 'bx-home',
      to: { name: 'admin' },
      requiredPermission: { resource: 'admin_panel', action: 'access' },
    },
  ]

  // Filter out items that require permissions the user doesn't have
  return allItems.filter(item => {
    if (item.type !== 'navItem')
      return true
    if (!('requiredPermission' in item))
      return true
    const permission = item.requiredPermission as { resource: string, action: string }

    return can(permission.resource, permission.action)
  })
})

// Helper to check if a menu item is active
const isMenuItemActive = (to: RouteLocationRaw | undefined): boolean => {
  if (typeof to !== 'object' || !to || !('name' in to))
    return false

  const toRoute = to as { name: string, query?: Record<string, string>, params?: Record<string, string> }

  // Check if route names match
  if (route.name !== toRoute.name)
    return false

  // If the route has query params, check if they match
  if (toRoute.query)
    return Object.keys(toRoute.query).every(key => route.query[key] === toRoute.query![key])

  // If no query params specified, only match if current route also has no relevant query params
  return !route.query.tab
}

// Mega menu states
const isKampenMegaOpen = ref(false)
const isOntdekMegaOpen = ref(false)
const isAanbodMegaOpen = ref(false)

// Close all menus when route changes
watch(() => route.fullPath, () => {
  isKampenMegaOpen.value = false
  isOntdekMegaOpen.value = false
  isAanbodMegaOpen.value = false
})

interface SimpleNavItem {
  label: string
  to?: RouteLocationRaw
  icon?: string
  children?: SimpleNavItem[]
}

// Mobile menu interfaces matching MobileMenu component
interface SubmenuItem {
  name: string
  href?: string
  to?: RouteLocationRaw
  icon: string
  label?: string
  items?: SubmenuItem[]
}

interface MenuItem {
  name: string
  href?: string
  to?: RouteLocationRaw
  icon: string
  submenu?: SubmenuItem[]
  label?: string
}

// Extract kampen columns from centralized navigation
const kampenColumns = computed(() => {
  const kampenItem = navigation.value.find(item => item.title === 'KampFabrik')
  if (!kampenItem?.children)
    return []

  // Map children to column format (skip "Alle kampen" item)
  return kampenItem.children
    .filter(child => child.children) // Only items with submenus (Leeftijd, Periode, Locatie, Thema)
    .map(child => ({
      title: child.title,
      icon: child.icon?.icon || 'bx-grid-alt',
      items: (child.children || []).map(subItem => ({
        label: subItem.title,
        to: subItem.to || { name: 'kampen' },
      })),
    }))
})

// Extract ontdek 't SpeelFabrik items from centralized navigation (preserve nested structure for desktop)
const ontdekSpeelFabrikItems = computed(() => {
  const ontdekItem = navigation.value.find(item => item.title === 'Ontdek \'t SpeelFabrik')
  if (!ontdekItem?.children)
    return []

  const items: SimpleNavItem[] = []

  ontdekItem.children.forEach(child => {
    const item: SimpleNavItem = {
      label: child.title,
      to: child.to ? (child.to as RouteLocationRaw) : undefined,
      icon: child.icon?.icon || 'bx-info-circle',
    }

    // If child has nested items (like Praktische Info), preserve them
    if (child.children) {
      item.children = child.children.map(subChild => ({
        label: subChild.title,
        to: (subChild.to || { name: 'index' }) as RouteLocationRaw,
        icon: subChild.icon?.icon || 'bx-info-circle',
      }))
    }

    items.push(item)
  })

  return items
})

// Guard and filter routes to avoid crashes if a named route was moved/renamed
const safeTo = (to?: RouteLocationRaw): RouteLocationRaw | string => {
  try {
    if (!to)
      return '/'

    if (typeof to === 'object' && to && 'name' in to && typeof to.name === 'string') {
      const name = to.name as string
      if (router.hasRoute(name))
        return to

      return '/'
    }

    return to
  }
  catch {
    return '/'
  }
}

// Mobile menu navigation structure from centralized config
const mobileNavigation = computed<MenuItem[]>(() => {
  return navigation.value.map(item => {
    // Base mobile menu item
    const mobileItem: MenuItem = {
      name: item.title,
      label: item.title,
      icon: item.icon?.icon || 'bx-star',
    }

    // Add 'to' if it exists
    if (item.to)
      mobileItem.to = item.to as RouteLocationRaw

    // Handle children (submenu)
    if (item.children) {
      // Special handling for KampFabrik (has nested structure)
      if (item.title === 'KampFabrik') {
        mobileItem.to = { name: 'kampen' } as RouteLocationRaw
        mobileItem.submenu = item.children.map(child => {
          const submenuItem: SubmenuItem = {
            name: child.title,
            label: child.title,
            icon: child.icon?.icon || 'bx-grid-alt',
          }

          if (child.to)
            submenuItem.to = child.to as RouteLocationRaw

          // Handle nested items (Leeftijd, Periode, Thema)
          if (child.children) {
            submenuItem.items = child.children.map(subChild => ({
              name: subChild.title,
              label: subChild.title,
              to: subChild.to as RouteLocationRaw,
              href: typeof subChild.to === 'object' && subChild.to && 'name' in subChild.to && typeof subChild.to.name === 'string' ? `/${subChild.to.name}` : undefined,
              icon: subChild.icon?.icon || 'bx-grid-alt',
            }))
          }

          return submenuItem
        })
      }
      else {
        // Regular submenu (Ontdek 't SpeelFabrik, etc.)
        mobileItem.submenu = item.children.map(child => {
          const submenuItem: SubmenuItem = {
            name: child.title,
            label: child.title,
            icon: child.icon?.icon || 'bx-star',
          }

          if (child.to)
            submenuItem.to = child.to as RouteLocationRaw

          // Handle nested items (Praktische Info, etc.)
          if (child.children) {
            submenuItem.items = child.children.map(subChild => ({
              name: subChild.title,
              label: subChild.title,
              to: subChild.to as RouteLocationRaw,
              href: typeof subChild.to === 'object' && subChild.to && 'name' in subChild.to && typeof subChild.to.name === 'string' ? `/${subChild.to.name}` : undefined,
              icon: subChild.icon?.icon || 'bx-file',
            }))
          }

          return submenuItem
        })
      }
    }

    return mobileItem
  })
})
</script>

<template>
  <!-- 👉 Navbar for desktop and mobile devices  -->
  <div class="front-page-navbar">
    <div class="front-page-navbar">
      <VAppBar
        data-allow-mismatch :color="$vuetify.theme.current.dark
          ? 'rgba(255, 255, 255, 0.04)'
          : 'rgba(var(--v-theme-surface), 0.38)'
        " :class="y > 10
          ? 'app-bar-scrolled'
          : [
            $vuetify.theme.current.dark ? 'app-bar-dark' : 'app-bar-light',
            'elevation-0',
          ]
        " class="navbar-blur" height="60"
      >
        <!-- toggle icon for mobile device -->
        <MobileMenu id="mobile-nav-menu" :navigation="mobileNavigation" class="d-inline-block d-md-none" />

        <VSpacer class="d-md-none" />

        <!-- Left Menu - Desktop -->
        <div
          ref="navbarLeftMenuRef" class="text-base align-center d-none d-md-flex navbar-left-menu"
          style=" overflow: hidden;flex-shrink: 1;"
        >
          <!-- Welkom -->
          <NuxtLink
            :to="{ name: 'index', hash: '#home' }"
            class="nav-link font-weight-medium py-2 px-3 d-flex align-center gap-2" :class="[
              props.activeId?.toLocaleLowerCase() === 'home'
                ? 'active-link'
                : '',
            ]"
          >
            <VIcon icon="bx-home" size="18" />
            Welkom
          </NuxtLink>

          <!-- Wide Desktop: Show separate Fabrik items (>= 1440px) -->
          <template v-if="!isNarrowDesktop">
            <!-- KampFabrik Menu (desktop) - Nested Dropdown -->
            <NuxtLink
              :to="{ name: 'kampen' }"
              class="font-weight-medium cursor-pointer px-3 py-2 dropdown-menu-item d-flex align-center"
              :class="isKampenMegaOpen ? 'active-link' : ''"
              style="color: rgba(var(--v-theme-on-surface)); text-decoration: none;"
            >
              <VIcon icon="bx-grid-alt" size="18" class="me-2" />
              KampFabrik
              <VIcon
                icon="bx-chevron-down" size="16" class="ms-2 dropdown-icon"
                :class="{ 'icon-rotated': isKampenMegaOpen }"
                @click.prevent.stop="isKampenMegaOpen = !isKampenMegaOpen"
              />
              <VMenu
                v-model="isKampenMegaOpen" :open-on-hover="!isTouchDevice" :close-on-content-click="false"
                activator="parent" transition="fade-transition" location="bottom start" offset="8"
              >
                <VList class="py-2" min-width="220">
                  <!-- Alle kampen (direct link) -->
                  <VListItem :to="{ name: 'kampen' }" class="submenu-item">
                    <template #prepend>
                      <VIcon icon="bx-book-open" size="18" />
                    </template>
                    <VListItemTitle>Alle kampen</VListItemTitle>
                  </VListItem>

                  <VDivider class="my-1" />

                  <!-- Dynamic menu items (Leeftijd, Periode, Locatie, Thema) -->
                  <VListItem
                    v-for="column in kampenColumns" :key="column.title" class="submenu-item has-submenu"
                    :link="false"
                  >
                    <template #prepend>
                      <VIcon :icon="column.icon" size="18" />
                    </template>
                    <VListItemTitle>{{ column.title }}</VListItemTitle>
                    <template #append>
                      <VIcon icon="bx-chevron-right" size="16" />
                    </template>

                    <VMenu
                      :open-on-hover="!isTouchDevice" :close-on-content-click="false" activator="parent"
                      transition="fade-transition" location="end top" offset="4" :open-delay="50"
                    >
                      <VList
                        class="py-2" min-width="200" :max-height="column.title === 'Periode' ? '500' : undefined"
                        :style="column.title === 'Periode' ? 'overflow-y: auto;' : undefined"
                      >
                        <VListItem :to="{ name: 'kampen' }" class="submenu-item">
                          <VListItemTitle>
                            {{
                              column.title === 'Leeftijd' ? 'Alle leeftijden'
                              : column.title === 'Periode' ? 'Alle periodes'
                                : column.title === 'Locatie' ? 'Alle locaties'
                                  : column.title === 'Thema' ? 'Alle thema\'s'
                                    : 'Alles'
                            }}
                          </VListItemTitle>
                        </VListItem>
                        <VDivider class="my-1" />
                        <VListItem v-for="(item, idx) in column.items" :key="idx" :to="item.to" class="submenu-item">
                          <VListItemTitle>{{ item.label }}</VListItemTitle>
                        </VListItem>
                      </VList>
                    </VMenu>
                  </VListItem>
                </VList>
              </VMenu>
            </NuxtLink>

            <!-- ZwemFabrik -->
            <NuxtLink :to="{ name: 'zwemmen' }" class="nav-link font-weight-medium py-2 px-3 d-flex align-center gap-2">
              <VIcon icon="bx-swim" size="18" />
              ZwemFabrik
            </NuxtLink>

            <!-- FeestFabrik -->
            <NuxtLink :to="{ name: 'feesten' }" class="nav-link font-weight-medium py-2 px-3 d-flex align-center gap-2">
              <VIcon icon="bx-party" size="18" />
              FeestFabrik
            </NuxtLink>
          </template>

          <!-- Narrow Desktop: Show consolidated "Aanbod" menu (960-1439px) -->
          <template v-else>
            <span
              class="font-weight-medium cursor-pointer px-3 py-2 dropdown-menu-item d-flex align-center"
              :class="isAanbodMegaOpen ? 'active-link' : ''" style="color: rgba(var(--v-theme-on-surface));"
            >
              <VIcon icon="bx-grid" size="18" class="me-2" />
              Aanbod
              <VIcon
                icon="bx-chevron-down" size="16" class="ms-2 dropdown-icon"
                :class="{ 'icon-rotated': isAanbodMegaOpen }"
              />
              <VMenu
                v-model="isAanbodMegaOpen" :open-on-hover="!isTouchDevice" :close-on-content-click="false"
                activator="parent" transition="fade-transition" location="bottom start" offset="8"
              >
                <VList class="py-2" min-width="220">
                  <!-- KampFabrik with nested hover submenu -->
                  <VListItem :to="{ name: 'kampen' }" class="submenu-item has-submenu">
                    <template #prepend>
                      <VIcon icon="bx-grid-alt" size="18" />
                    </template>
                    <VListItemTitle>KampFabrik</VListItemTitle>
                    <template #append>
                      <VIcon icon="bx-chevron-right" size="16" />
                    </template>

                    <!-- Nested submenu for KampFabrik categories -->
                    <VMenu
                      :open-on-hover="!isTouchDevice" :close-on-content-click="false" activator="parent"
                      transition="fade-transition" location="end top" offset="4" :open-delay="50"
                    >
                      <VList class="py-2" min-width="200">
                        <!-- Alle kampen -->
                        <VListItem :to="{ name: 'kampen' }" class="submenu-item">
                          <VListItemTitle>Alle kampen</VListItemTitle>
                        </VListItem>

                        <VDivider class="my-1" />

                        <!-- Dynamic menu items (Leeftijd, Periode, Locatie, Thema) -->
                        <VListItem
                          v-for="column in kampenColumns" :key="column.title" class="submenu-item has-submenu"
                          :link="false"
                        >
                          <template #prepend>
                            <VIcon :icon="column.icon" size="16" />
                          </template>
                          <VListItemTitle>{{ column.title }}</VListItemTitle>
                          <template #append>
                            <VIcon icon="bx-chevron-right" size="14" />
                          </template>

                          <VMenu
                            :open-on-hover="!isTouchDevice" :close-on-content-click="false" activator="parent"
                            transition="fade-transition" location="end top" offset="4" :open-delay="50"
                          >
                            <VList
                              class="py-2" min-width="200"
                              :max-height="column.title === 'Periode' ? '500' : undefined"
                              :style="column.title === 'Periode' ? 'overflow-y: auto;' : undefined"
                            >
                              <VListItem :to="{ name: 'kampen' }" class="submenu-item">
                                <VListItemTitle>
                                  {{
                                    column.title === 'Leeftijd' ? 'Alle leeftijden'
                                    : column.title === 'Periode' ? 'Alle periodes'
                                      : column.title === 'Locatie' ? 'Alle locaties'
                                        : column.title === 'Thema' ? 'Alle thema\'s'
                                          : 'Alles'
                                  }}
                                </VListItemTitle>
                              </VListItem>
                              <VDivider class="my-1" />
                              <VListItem
                                v-for="(item, idx) in column.items" :key="idx" :to="item.to"
                                class="submenu-item"
                              >
                                <VListItemTitle>{{ item.label }}</VListItemTitle>
                              </VListItem>
                            </VList>
                          </VMenu>
                        </VListItem>
                      </VList>
                    </VMenu>
                  </VListItem>

                  <VDivider class="my-1" />

                  <!-- ZwemFabrik -->
                  <VListItem :to="{ name: 'zwemmen' }" class="submenu-item">
                    <template #prepend>
                      <VIcon icon="bx-swim" size="18" />
                    </template>
                    <VListItemTitle>ZwemFabrik</VListItemTitle>
                  </VListItem>

                  <!-- FeestFabrik -->
                  <VListItem :to="{ name: 'feesten' }" class="submenu-item">
                    <template #prepend>
                      <VIcon icon="bx-party" size="18" />
                    </template>
                    <VListItemTitle>FeestFabrik</VListItemTitle>
                  </VListItem>
                </VList>
              </VMenu>
            </span>
          </template>

          <!-- Ontdek 't SpeelFabrik Menu (desktop) - Nested Dropdown -->
          <span
            class="font-weight-medium cursor-pointer px-3 py-2 dropdown-menu-item d-flex align-center"
            :class="[isOntdekMegaOpen || props.activeId?.toLocaleLowerCase() === 'ontdek' ? 'active-link' : '']"
            style="color: rgba(var(--v-theme-on-surface));"
          >
            <VIcon icon="bx-info-circle" size="18" class="me-2" />
            Ontdek 't SpeelFabrik
            <VIcon
              icon="bx-chevron-down" size="16" class="ms-2 dropdown-icon"
              :class="{ 'icon-rotated': isOntdekMegaOpen }"
            />
            <VMenu
              v-model="isOntdekMegaOpen" :open-on-hover="!isTouchDevice" :close-on-content-click="false"
              activator="parent" transition="fade-transition" location="bottom start" offset="8"
            >
              <VList class="py-2" min-width="240">
                <template v-for="(it, iIdx) in ontdekSpeelFabrikItems" :key="iIdx">
                  <!-- Item with nested submenu -->
                  <VListItem v-if="it.children" class="submenu-item has-submenu" :link="false">
                    <template #prepend>
                      <VIcon :icon="it.icon || 'bx-info-circle'" size="18" />
                    </template>
                    <VListItemTitle>{{ it.label }}</VListItemTitle>
                    <template #append>
                      <VIcon icon="bx-chevron-right" size="16" />
                    </template>

                    <VMenu
                      :open-on-hover="!isTouchDevice" :close-on-content-click="false" activator="parent"
                      transition="fade-transition" location="end top" offset="4" :open-delay="50"
                    >
                      <VList class="py-2" min-width="200">
                        <VListItem
                          v-for="(subIt, subIdx) in it.children" :key="subIdx" :to="subIt.to"
                          class="submenu-item"
                        >
                          <template #prepend>
                            <VIcon :icon="subIt.icon || 'bx-circle'" size="16" />
                          </template>
                          <VListItemTitle>{{ subIt.label }}</VListItemTitle>
                        </VListItem>
                      </VList>
                    </VMenu>
                  </VListItem>

                  <!-- Regular item without nested submenu -->
                  <VListItem v-else :to="it.to" class="submenu-item">
                    <template #prepend>
                      <VIcon :icon="it.icon || 'bx-info-circle'" size="18" />
                    </template>
                    <VListItemTitle>{{ it.label }}</VListItemTitle>
                  </VListItem>
                </template>
              </VList>
            </VMenu>
          </span>

          <!-- Contacteer ons -->
          <NuxtLink
            v-if="showContactText"
            :to="{ name: 'contact' }" class="nav-link font-weight-medium py-2 px-3 d-flex align-center gap-2"
            :class="[
              props.activeId?.toLocaleLowerCase() === 'contact'
                ? 'active-link'
                : '',
            ]"
          >
            <VIcon icon="bx-envelope" size="18" />
            Contacteer ons
          </NuxtLink>
        </div>

        <VSpacer class="d-none d-md-flex" style="flex-shrink: 0;" />

        <!-- Right Side Icons -->
        <div class="d-flex align-center gap-x-2 gap-x-md-4" style="flex-shrink: 0; min-inline-size: fit-content;">
          <!-- Contact icon (only when menu is compacted) -->
          <VBtn
            v-if="!showContactText" icon variant="text" :to="{ name: 'contact' }"
            :class="[props.activeId?.toLocaleLowerCase() === 'contact' ? 'active-link' : '']"
            aria-label="Contacteer ons"
          >
            <VIcon icon="bx-envelope" size="20" />
          </VBtn>
          <!-- Cart Button -->
          <VChip size="large" color="primary" variant="text" label @click="router.push('/cart')">
            <VIcon icon="bx-cart" />
            <span v-if="cartParticipantCount" style="padding-inline-start: 10px;">{{ cartParticipantCount }}</span>
          </VChip>
          <!-- Theme Switcher -->
          <NavbarThemeSwitcher />
          <!-- Divider -->
          <VDivider vertical />
          <!-- User Profile Menu -->
          <VBadge
            v-if="userData" dot bordered location="bottom right" offset-x="1" offset-y="2" color="primary"
            variant="tonal"
          >
            <VAvatar
              size="38" class="cursor-pointer" :color="!(userData && userData.avatar && !avatarLoadError) ? 'primary' : undefined"
              :variant="!(userData && userData.avatar && !avatarLoadError) ? 'tonal' : undefined"
            >
              <VImg v-if="userData && userData.avatar && !avatarLoadError" :src="userData.avatar" crossorigin="anonymous" @error="avatarLoadError = true" />
              <VIcon v-else icon="bx-user" />

              <!-- User Profile Menu -->
              <VMenu activator="parent" width="240" location="bottom end" offset="20px">
                <VList>
                  <VListItem>
                    <div class="d-flex gap-2 align-center">
                      <VListItemAction>
                        <VBadge dot location="bottom right" offset-x="3" offset-y="3" color="success" bordered>
                          <VAvatar
                            :color="!(userData && userData.avatar && !avatarLoadError) ? 'primary' : undefined
                            " :variant="!(userData && userData.avatar && !avatarLoadError) ? 'tonal' : undefined
                            "
                          >
                            <VImg v-if="userData && userData.avatar && !avatarLoadError" :src="userData.avatar" crossorigin="anonymous" @error="avatarLoadError = true" />
                            <VIcon v-else icon="bx-user" />
                          </VAvatar>
                        </VBadge>
                      </VListItemAction>
                      <div>
                        <VListItemTitle class="font-weight-medium">
                          {{ userData.fullName || userData.username }}
                        </VListItemTitle>
                        <VListItemSubtitle class="text-disabled text-capitalize">
                          {{ userData.role }}
                        </VListItemSubtitle>
                      </div>
                    </div>
                  </VListItem>

                  <PerfectScrollbar
                    :options="{ wheelPropagation: false, suppressScrollX: true }"
                    style="max-block-size: 400px;"
                  >
                    <template v-for="item in userProfileList" :key="item.title">
                      <VListItem
                        v-if="item.type === 'navItem'" :to="safeTo(item.to as RouteLocationRaw)" :value="item.title"
                        :active="isMenuItemActive(item.to as RouteLocationRaw)" exact
                      >
                        <template #prepend>
                          <VIcon :icon="item.icon" size="22" />
                        </template>

                        <VListItemTitle>{{ item.title }}</VListItemTitle>
                      </VListItem>

                      <VDivider v-else class="my-1" />
                    </template>
                    <VDivider class="my-1" />
                    <VListItem prepend-icon="bx-power-off" @click="logout">
                      Logout
                    </VListItem>
                  </PerfectScrollbar>
                </VList>
              </VMenu>
            </VAvatar>
          </VBadge>

          <!-- Login Button (when not logged in) -->
          <VBtn v-if="!userData" variant="tonal" color="primary" :to="{ name: 'auth-login' }">
            <VIcon icon="bx-user" class="me-2" />
            Log in
          </VBtn>
        </div>
      </VAppBar>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Apply Peachy Keen font to all navigation items
.navbar-left-menu,
.nav-link,
.dropdown-menu-item,
.page-link,
.kamp-menu-item,
.kamp-column-title,
.nested-menu-section {
  font-family: "Peachy Kee JF", sans-serif;
}

.nav-menu {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  gap: 3rem;

  >div {
    flex: 0 1 auto;
    min-inline-size: 0;
  }
}

.nav-link {
  &:not(:hover) {
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  }
}

.page-link {
  &:hover {
    color: rgb(var(--v-theme-primary)) !important;
  }
}

@media (max-width: 1280px) {
  .nav-menu {
    gap: 2.5rem;
  }
}

@media (min-width: 1920px) {
  .front-page-navbar {
    .v-toolbar {
      max-inline-size: calc(1440px - 32px);
    }
  }
}

@media (min-width: 1280px) and (max-width: 1919px) {
  .front-page-navbar {
    .v-toolbar {
      max-inline-size: calc(1200px - 32px);
    }
  }
}

@media (min-width: 960px) and (max-width: 1279px) {
  .front-page-navbar {
    .v-toolbar {
      max-inline-size: calc(900px - 32px);
    }
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .front-page-navbar {
    .v-toolbar {
      max-inline-size: calc(100% - 64px);
    }
  }
}

@media (max-width: 600px) {
  .front-page-navbar {
    .v-toolbar {
      max-inline-size: calc(100% - 32px);
    }
  }
}

.nav-item-img {
  border: 10px solid rgb(var(--v-theme-background));
  border-radius: 10px;
}

.active-link {
  color: rgb(var(--v-theme-primary)) !important;
}

.dropdown-icon {
  transition: transform 0.3s ease;
}

.icon-rotated {
  transform: rotate(180deg);
}

.dropdown-menu-item {
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    .dropdown-icon {
      transform: rotate(180deg);
    }
  }
}

.app-bar-light {
  border: 2px solid rgba(var(--v-theme-surface), 68%);
  border-radius: 0.375rem;
  transition: none;
}

.app-bar-dark {
  border: 2px solid rgba(255, 255, 255, 8%);
  border-radius: 0.375rem;
  transition: none;
}

.app-bar-scrolled {
  border: 2px solid rgb(var(--v-theme-surface));
  border-radius: 0.375rem;
  background-color: rgb(var(--v-theme-surface)) !important;
  transition: none;
}

.front-page-navbar::after {
  position: fixed;
  z-index: 2;
  backdrop-filter: saturate(100%) blur(6px);
  block-size: 5rem;
  content: "";
  inline-size: 100%;
}

.navbar-left-menu {
  white-space: nowrap;

  .nav-link,
  .dropdown-menu-item {
    font-size: 1.3rem;
  }
}

@media (max-width: 1280px) {
  .navbar-left-menu {
    .nav-link,
    .dropdown-menu-item {
      font-size: 1.2rem;
      padding-inline: 0.5rem !important;
    }
  }
}

.login-btn {
  box-shadow: 0 2px 8px rgba(var(--v-theme-primary), 0.3) !important;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.4) !important;
    transform: translateY(-2px);
  }
}

.search-bar-wrapper {
  position: absolute;
  z-index: 10;
  border-radius: 0.375rem;
  animation: slide-in 0.3s ease-out;
  background-color: rgb(var(--v-theme-surface));
  box-shadow: 0 4px 12px rgb(0 0 0 / 10%);
  inset-inline: 0;
  padding-inline: 1rem;

  .search-input {
    flex: 1;
  }
}

@keyframes slide-in {
  from {
    opacity: 0;
    transform: translateX(10px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>

<style lang="scss">
@use "@layouts/styles/mixins" as layoutMixins;

.front-page-navbar {
  .v-toolbar__content {
    padding-inline: 30px !important;
  }

  .v-toolbar {
    inset-inline: 0 !important;
    margin-block-start: 1rem !important;
    margin-inline: auto !important;
  }

  // Custom fade-in-up animation for menus
  .v-overlay__content {
    animation: fade-in-up 0.3s ease-out;
  }

  .v-list {
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    border-radius: 0.5rem;
    background-color: rgb(var(--v-theme-surface)) !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 10%);

    .v-divider {
      border-color: rgba(var(--v-theme-on-surface), 0.88);
    }
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mega-menu-item {
  &:hover {
    color: rgb(var(--v-theme-primary)) !important;
  }
}

.submenu-item {
  font-family: "Peachy Keen JF", sans-serif !important;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.08);
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.has-submenu {
    position: relative;
  }

  .v-list-item-title {
    font-family: "Peachy Keen JF", sans-serif !important;
  }

  .v-icon {
    opacity: 0.7;
  }

  &:hover .v-icon {
    color: rgb(var(--v-theme-primary));
    opacity: 1;
  }
}

.kamp-menu-item {
  display: inline-block;
  border-radius: 0.375rem;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-family: "Peachy Kee JF", sans-serif !important;
  padding-block: 0.375rem;
  padding-inline: 0.5rem;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.08);
    color: rgb(var(--v-theme-primary)) !important;
    transform: translateX(4px);
  }
}

.kamp-column-title {
  border-block-end: 2px solid rgb(var(--v-theme-primary));
  font-family: "Peachy Kee JF", sans-serif !important;
  letter-spacing: 0.5px;
}

.nested-menu-section {
  padding-block: 0.5rem;

  .text-high-emphasis {
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  }
}

/* Mobile menu custom styles */
#mobile-nav-menu {
  margin-inline: -0.75rem 0.5rem;

  .mobile-menu-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    border-radius: 0.375rem;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.08);
    }
  }
}
</style>
